import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  Form,
  InputGroup,
  SelectPicker,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { ProductServices } from "../../../core/services/ProductServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  ProductFields,
  ProductListParams,
  Permission,
} from "./ProductDataTypes";
import "./Products.css";
import {
  displayAmountField,
  isEmptyVal,
  isNotEmpty,
} from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import DescriptionCell from "../../components/Elements/DescriptionCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import { displayField } from "../../../utils/helpers";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  roleTypeArr,
  productGenderObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import StoreProductSingleAction from "./StoreProductSingleAction";
import { updateIn } from "json-immut";
import EditCell from "../../components/Elements/EditCell";
import EditCellBlur from "../../components/Elements/EditCellBlur";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => true;
}
const permissionSingleObj: Permission = {
  update: false,
  delete: false,
};

const permissionBulkObj: Permission = {
  delete: UtilityServices.hasPermission("delete-store-product"),
  back_to_list: true,
};
const StoreProductsForOrder = ({
  forStore,
  onProductChange,
  withSmartFilter = true,
  withHeader = true,
  orderProducts = [],
  skipInitialOnProductChange = false,
  forGender = "",
  setStopSubmit,
}: {
  forStore?: number;
  onProductChange?: Function;
  withSmartFilter?: boolean;
  withHeader?: boolean;
  orderProducts?: any[];
  skipInitialOnProductChange?: boolean;
  forGender?: string;
  setStopSubmit?: Function;
}) => {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const hasInitializedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page_1000);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ProductFields[]>([]);
  const [selectedOrderProducts, setSelectedOrderProducts] =
    useState<ProductFields[]>(orderProducts);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [categoryList, setCategoryList] = useState([]);
  const modalRef = useRef<ImageModal>(null);
  const [selectedProduct, setSelectedProductData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();

  const initSearchFormData = {
    product_category: "",
    product_for_gender: forGender,
    smart_search: "",
  };

  const [formData, setFormData] =
    useState<ProductListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([1]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  const getQtyFromOrderProducts = (store_product_id: number) => {
    if (!orderProducts || orderProducts.length === 0) {
      return 1;
    }
    const matched = orderProducts.find(
      (item: any) => item.store_product_id === store_product_id,
    );

    return matched?.product_qty ?? 1;
  };

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }
  const handleCheck = (value: number, checked: any) => {
    let keys: number[] = [];
    let oproducts = selectedOrderProducts;
    if (checked) {
      keys = [...checkedKeys, value];
      data.forEach(function (item: any) {
        if (item.id === value) {
          oproducts.push(item);
        }
      });
    } else {
      keys = checkedKeys.filter((item) => item !== value);
      oproducts = oproducts.filter((item) => item.id !== value);
    }
    setSelectedOrderProducts(oproducts);
    setCheckedKeys(keys);
    if (onProductChange) onProductChange(oproducts);
  };
  function getStoreId() {
    let storeId = null;
    if (forStore) {
      storeId = forStore;
    } else if (
      UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client &&
      !isEmptyVal(UtilityServices.getLoggedInUser("store_id"))
    ) {
      storeId = UtilityServices.getLoggedInUser("store_id");
    }
    return storeId;
  }

  async function getProductCategories() {
    try {
      const categoryRes =
        await ProductServices.getProductCategoriesByStore(getStoreId());
      setCategoryList(get("data.data", categoryRes));
    } catch (error) {
      console.log(error);
    }
  }

  async function getProducts(params: ProductListParams) {
    try {
      setLoading(true);
      if (setStopSubmit) setStopSubmit?.(true);
      params.with_sub_products = "1";
      const res = await ProductServices.getStoreProducts(params);
      const rawData = get("data.data", res) || [];
      const normalizedData = rawData.map((row: any) => {
        const qty = getResolvedQty(row);

        return {
          ...row,
          product_qty: qty,
          total_product_price: qty * Number(row.product_price || 0),
        };
      });

      setData(normalizedData);
      setTotal(get("data.total", res));
      setLoading(false);
      if (setStopSubmit) setStopSubmit(false);
    } catch (error: any) {
      setLoading(false);
      if (setStopSubmit) setStopSubmit(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    } finally {
      setLoading(false);
      setStopSubmit?.(false);
    }
  }

  async function init() {
    try {
      getProducts(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    const storeId = getStoreId();
    if (isEmptyVal(storeId)) {
      UtilityServices.showToaster({
        type: "warning",
        message: t("REQUIRED_SELECT", {
          field: t("STORE").toLowerCase(),
        }),
      });
      setData([]);
      return;
    } else {
      getProductCategories();
      setCheckedExistingItems(orderProducts);
      init();
      if (!skipInitialOnProductChange && !hasInitializedRef.current) {
        onProductChange?.(orderProducts);
      }

      hasInitializedRef.current = true;
    }
  }, [forStore]);

  useEffect(() => {
    if (forGender) {
      setFormData((prev) => ({
        ...prev,
        product_for_gender: forGender,
      }));
    }
  }, [forGender]);

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function setCheckedExistingItems(items: any[]) {
    let keys: number[] = [];
    items.forEach((it: any) => {
      keys.push(it.store_product_id);
    });
    setCheckedKeys(keys);
  }

  function productPicModal(value: any) {
    if (
      !failedImages.includes(value.large_image_url) &&
      value.large_image_url != null &&
      value.large_image_url !== ""
    ) {
      setSelectedProductData(value);
      modalRef.current?.openModal();
      modalRef.current?.hideDelete();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: ProductListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }

    params.for_store = getStoreId();

    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getProducts(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getProducts(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      }),
    );
  }

  const filterProducts = (e: any, search_form_data?: any) => {
    if (e.key === "Enter" || e.type === "click") {
      let params: any = {
        smart_search: smartSearchInput,
        product_category: formData?.product_category,
        product_for_gender: formData?.product_for_gender,
        rows: rows,
      };
      if (search_form_data?.product_category !== undefined) {
        params.product_category = search_form_data.product_category;
      }

      if (search_form_data?.product_for_gender !== undefined) {
        params.product_for_gender = search_form_data.product_for_gender;
      }

      getProducts(
        getSearchParams({
          ...params,
        }),
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setFormData({ ...initSearchFormData, ...{ product_for_gender: "" } });
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page_1000);
    getProducts(
      getSearchParams({
        smart_search: "",
        product_category: "",
        product_for_gender: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page_1000,
      }),
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getProducts(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  };

  const getRowTotal = (row: any) => {
    const price = Number(row.product_price) || 0;
    const qty = getResolvedQty(row);
    const total = price * qty;
    return total;
  };

  const handleCellChange = (
    idv: number,
    key: string,
    value: any,
    source?: "blur",
  ) => {
    let finalQty = Number(value);

    if (!Number.isInteger(finalQty) || finalQty <= 0) {
      finalQty = 1;
    }

    const newData = data.map((item: any) => {
      if (item.id === idv) {
        const price = Number(item.product_price) || 0;
        return {
          ...item,
          [key]: finalQty,
          total_product_price: finalQty * price,
        };
      }
      return item;
    });

    setData(newData);

    const updatedSelectedProducts = selectedOrderProducts.map((item: any) => {
      if (item.id === idv) {
        const price = Number(item.product_price) || 0;
        return {
          ...item,
          [key]: finalQty,
          total_product_price: finalQty * price,
        };
      }
      return item;
    });

    setSelectedOrderProducts(updatedSelectedProducts);

    onProductChange?.(updatedSelectedProducts);
  };

  const getResolvedQty = (row: any) => {
    if (row.product_qty !== undefined && row.product_qty !== null) {
      return Number(row.product_qty);
    }
    return getQtyFromOrderProducts(row.id);
  };

  const calculateRowTotal = (row: any) => {
    const price = Number(row.product_price) || 0;
    const qty = Number(row.product_qty) || 1;
    return price * qty;
  };

  return (
    <div className="products">
      <Container className="container">
        {withHeader && (
          <Header className="header">
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item colspan={8}>
                <h4>{t("PRODUCTS")}</h4>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Header>
        )}

        <Content>
          <div className="row">
            {withSmartFilter && (
              <div className="col-md-8 dbl-dd-bx">
                <Form
                  formValue={formData}
                  onChange={(data: any) => {
                    let form = { ...formData, ...data };
                    setFormData(form);
                  }}
                  layout="inline"
                >
                  <InputGroup>
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("NAME").toLowerCase() +
                          " " +
                          t("OR").toLowerCase() +
                          " " +
                          t("SKU").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterProducts(e)}
                    />
                    <SelectPicker
                      data={categoryList}
                      valueKey="id"
                      labelKey="cat_name"
                      onSelect={(value, item, e) => {
                        handleFormChanges({ path: "product_category", value });
                        filterProducts(e, { product_category: value });
                      }}
                      onClean={(e: any) => {
                        handleFormChanges({
                          path: "product_category",
                          value: "",
                        });
                        filterProducts(e, { product_category: "" });
                      }}
                      name="product_category"
                      value={formData?.product_category}
                      placeholder={t("CATEGORY")}
                    />
                    <SelectPicker
                      data={productGenderObjArr}
                      valueKey="value"
                      labelKey="label"
                      onSelect={(value, item, e) => {
                        handleFormChanges({
                          path: "product_for_gender",
                          value,
                        });
                        filterProducts(e, { product_for_gender: value });
                      }}
                      onClean={(e: any) => {
                        handleFormChanges({
                          path: "product_for_gender",
                          value: "",
                        });
                        filterProducts(e, { product_for_gender: "" });
                      }}
                      name="product_for_gender"
                      value={formData?.product_for_gender}
                      placeholder={t("FOR") + " " + t("GENDER")}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterProducts(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </Form>
              </div>
            )}
          </div>

          <div className={total > 0 ? "usr-tbl-border" : "no-record-table"}>
            <Table
              height={300}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              <Column width={30} align="center" fixed>
                <CustomHeaderCell>
                  <div style={{ lineHeight: "40px" }}></div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={50} align="center">
                <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
                <ImageCellFormatter
                  dataKey="large_image_url"
                  rowData={(rowData: any) => rowData}
                  dummyImage={loggedInUserDetail.product_default_image}
                  onFailedImages={getFailedImages}
                  openPictureModal={productPicModal}
                />
              </Column>
              <Column width={150} sortable resizable>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="product_name">
                  {(rowData: any) => (
                    <span
                      title={
                        (isNotEmpty(`${rowData.product_sku}`) &&
                          `${rowData.product_sku}`) ||
                        `${rowData.product_name}`
                      }
                    >
                      {displayField(rowData.product_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={90}>
                <CustomHeaderCell>
                  {t("ORIGINAL")} {t("PRICE")}
                </CustomHeaderCell>
                <CustomCell dataKey="product_original_price">
                  {(rowData: any) => (
                    <span>
                      {displayAmountField(rowData.product_original_price)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={60}>
                <CustomHeaderCell>{t("PRICE")}</CustomHeaderCell>
                <CustomCell dataKey="product_price">
                  {(rowData: any) => (
                    <span>{displayAmountField(rowData.product_price)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={70}>
                <CustomHeaderCell>{t("QTY")}</CustomHeaderCell>
                <EditCellBlur
                  dataKey="product_qty"
                  onChange={handleCellChange}
                />
              </Column>
              <Column width={60}>
                <CustomHeaderCell>{t("TOTAL")}</CustomHeaderCell>
                <CustomCell dataKey="total_product_price">
                  {(rowData: any) => (
                    <span>
                      {displayAmountField(rowData.total_product_price)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={120} sortable>
                <CustomHeaderCell>{t("CATEGORY")}</CustomHeaderCell>
                <CustomCell dataKey="cat_name" />
              </Column>
              <Column flexGrow={1}>
                <CustomHeaderCell>{t("DESCRIPTION")}</CustomHeaderCell>
                <DescriptionCell
                  rowData={(rowData: any) => rowData}
                  dataIndex={"product_detail"}
                  introLength={300}
                />
              </Column>
              <Column width={50} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <StoreProductSingleAction
                      productData={rowData}
                      permission={permissionSingleObj}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={t("PRODUCT") + " " + t("IMAGE")}
        heading={t("PRODUCT") + " " + t("IMAGE")}
        body={
          <img
            src={selectedProduct.large_image_url}
            className="main-profile-pic"
            alt={t("PROFILE_PIC")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
    </div>
  );
};

export default StoreProductsForOrder;
